import { useState } from 'react';

import SvgIcon, { SvgIconProps } from '@mui/material/SvgIcon';

// Module-level, monotonically increasing -- guarantees a distinct id per
// mounted instance with no dependency on React internals (see clipId note
// below for why useId() was dropped in favor of this).
let nextClipInstanceId = 0;

export function TruckIcon(props: SvgIconProps) {
  // A fixed clipPath id breaks once this icon renders more than once in the
  // same document -- normal HTML tolerates it (browsers fall back to the
  // FIRST matching id), but printing to PDF does not: the Cronograma export
  // renders this icon once per physical page (plus an off-screen copy used
  // to measure header height, TimelineGantt's measuredHeaderHeight), and
  // every instance's clip-path silently broke, making the icon disappear
  // entirely on every page (QA screenshot, 2026-08-11).
  //
  // Originally switched to useId(), then to useId().replace(/:/g, '') (the
  // colon in useId()'s own output like ":r0:" breaks a CSS selector). Both
  // still failed intermittently on one arbitrary page out of several (QA
  // 2026-08-12, twice, on a DIFFERENT page each time) -- useId() is designed
  // to match server- and client-rendered ids for hydration, a problem this
  // page doesn't have (Puppeteer only waits for a plain CSR render, there's
  // no server HTML to reconcile against), so its ordering guarantees don't
  // actually cover this use case. A plain incrementing counter sidesteps the
  // whole class of bug: it can never repeat within one page load.
  const [clipId] = useState(() => `truck-icon-clip-${nextClipInstanceId++}`);
  return (
    <SvgIcon
      {...props}
      stroke='none'
      viewBox='0 0 24 24'
    >
      <g clipPath={`url(#${clipId})`}>
        <path
          d='M21.6 11.22L17 7.52V5C17.0245 4.49494 16.8477 4.00078 16.5084 3.62587C16.1691 3.25095 15.695 3.02588 15.19 3H3.78998C3.28855 3.03096 2.8195 3.25823 2.48446 3.63258C2.14941 4.00693 1.97535 4.49822 1.99998 5V15C1.9907 15.3971 2.09987 15.788 2.31359 16.1228C2.5273 16.4576 2.83588 16.7212 3.19998 16.88C3.01581 17.3305 2.94404 17.8191 2.99085 18.3036C3.03765 18.7881 3.20164 19.2539 3.46865 19.6609C3.73566 20.0678 4.09769 20.4037 4.52348 20.6395C4.94927 20.8753 5.42605 21.004 5.91267 21.0144C6.39929 21.0249 6.88114 20.9167 7.31665 20.6994C7.75215 20.482 8.12823 20.1619 8.41243 19.7668C8.69663 19.3717 8.88042 18.9133 8.94794 18.4313C9.01546 17.9492 8.96468 17.458 8.79998 17H15.16C14.9955 17.4531 14.9427 17.9392 15.0059 18.4171C15.0692 18.895 15.2466 19.3506 15.5233 19.7454C15.8 20.1401 16.1677 20.4624 16.5953 20.6849C17.023 20.9074 17.4979 21.0235 17.98 21.0235C18.462 21.0235 18.937 20.9074 19.3646 20.6849C19.7923 20.4624 20.16 20.1401 20.4367 19.7454C20.7133 19.3506 20.8908 18.895 20.954 18.4171C21.0173 17.9392 20.9644 17.4531 20.8 17H21C21.2652 17 21.5196 16.8946 21.7071 16.7071C21.8946 16.5196 22 16.2652 22 16V12C21.9969 11.8481 21.9593 11.699 21.89 11.5638C21.8207 11.4287 21.7215 11.3111 21.6 11.22V11.22ZM20 12.48V15H17V10.08L20 12.48ZM6.99998 18C6.99998 18.1978 6.94133 18.3911 6.83145 18.5556C6.72157 18.72 6.56539 18.8482 6.38266 18.9239C6.19994 18.9996 5.99887 19.0194 5.80489 18.9808C5.61091 18.9422 5.43273 18.847 5.29287 18.7071C5.15302 18.5673 5.05778 18.3891 5.01919 18.1951C4.98061 18.0011 5.00041 17.8 5.0761 17.6173C5.15179 17.4346 5.27996 17.2784 5.44441 17.1685C5.60886 17.0586 5.8022 17 5.99998 17C6.2652 17 6.51955 17.1054 6.70709 17.2929C6.89462 17.4804 6.99998 17.7348 6.99998 18ZM12 15H3.99998V5H15V15H12ZM19 18C19 18.1978 18.9413 18.3911 18.8314 18.5556C18.7216 18.72 18.5654 18.8482 18.3827 18.9239C18.1999 18.9996 17.9989 19.0194 17.8049 18.9808C17.6109 18.9422 17.4327 18.847 17.2929 18.7071C17.153 18.5673 17.0578 18.3891 17.0192 18.1951C16.9806 18.0011 17.0004 17.8 17.0761 17.6173C17.1518 17.4346 17.28 17.2784 17.4444 17.1685C17.6089 17.0586 17.8022 17 18 17C18.2652 17 18.5196 17.1054 18.7071 17.2929C18.8946 17.4804 19 17.7348 19 18Z'
          fill={props.stroke ?? props.fill ?? 'white'}
        />
      </g>
      <defs>
        <clipPath id={clipId}>
          <rect
            width='24'
            height='24'
            fill={props.stroke ?? props.fill ?? 'white'}
          />
        </clipPath>
      </defs>
    </SvgIcon>
  );
}
